/**
 * Guard entity + per-tick update. Handles patrol + wander + investigation
 * walking + step-locked movement (same tile-locked moveProgress model as
 * the player). Combat is layered on top in main.ts.
 *
 * Headless — no PixiJS, no DOM. See CLAUDE.md architecture rule 4.
 */

import { CONFIG } from '../config';
import { isBlindStunned } from './blind';
import { updateDoorMemory } from './doorWatch';
import { dirIndex, rotateFacing } from './input';
import type { BloodTrackState } from './bloodTrail';
import { createHumanoidBody, movementMultiplier, type BodyState } from './body';
import type { Item } from './inventory';
import { inBounds, isBlocked, npcOpenableDoor, tileAt, toggleDoor, type MapData } from './map';
import type { EmitSound } from './movement';
import { syncVisual } from './movement';
import { coverCostBias } from './cover';
import { computeLightField, isUnlit, lightLevelAt } from './light';
import { findPath, type Point } from './pathfind';
import type { Stance } from './player';
import { blocksVision, isCoverAnchor, TileType } from './tiles';
import { createGuardPistol, type Weapon } from './weapons';
import type { Entity, World } from './world';

export type GuardState = 'unaware' | 'suspicious' | 'cautious' | 'alert';

/** Loop of tile waypoints. Guard walks 0→1→…→N-1→0. */
export type PatrolRoute = readonly Point[];

/** Cached A* result the guard is currently following. */
interface CachedPath {
  steps: Point[];
  /** Index into `steps` matching guard.x/y right now. */
  idx: number;
  targetX: number;
  targetY: number;
  /** True if the path was planned with adjacentGoal (stop 1 tile short). */
  adjacent: boolean;
}

export interface Guard extends Entity {
  facingDx: number;
  facingDy: number;
  /** Combat stance. Guards use `'walk'` by default and `'crouch'` while ducking
   *  behind cover (see `updateGuardCover`). Guards never run. Crouch lowers
   *  their speed + vision and — behind cover — hides them and blocks their own
   *  sight (so they must peek/stand to shoot). */
  stance: Stance;
  /** Seconds elapsed in the current duck/peek phase while using cover. */
  peekTimer: number;
  /** True while peeking (standing to take a shot); false while ducked. */
  peeking: boolean;
  /** Set each tick by the player-visibility pass: true when the player cannot
   *  currently see this guard because it's crouched behind cover. Renderer +
   *  focus treat a hidden guard as not-currently-visible. */
  hiddenByCover: boolean;
  /** Set each tick: true when the player currently PERCEIVES this guard — in
   *  the view cone (and not cover-hidden), heard (audible), or marked. This is
   *  the set the player can focus/aim and keep focus on. See
   *  `updatePlayerPerception`. */
  perceivedByPlayer: boolean;
  /** Ticks remaining before the guard picks a new wander direction. */
  wanderCooldown: number;
  /** Seconds remaining to show the "alerting others" (#) icon over the guard. */
  alertingTimer: number;
  /** True for the guard tasked with getting to the fusebox to restore power
   *  after a blackout. Overrides normal patrol/investigation until done. */
  restorePower: boolean;
  /** Current "take cover in the dark" target tile (a walkable tile beside a
   *  wall/cover), or null. Guards reposition between these while hunkering. */
  coverTargetX: number | null;
  coverTargetY: number | null;
  /** Ticks until the guard picks a fresh dark-cover spot (moves between cover). */
  coverSeekTimer: number;
  /** Set for one tick when the guard's move was blocked by the PLAYER (a bump).
   *  main.ts reacts (recognise someone's there / engage). */
  bumpedPlayer: boolean;
  /** True once this guard has personally been in the dark during a blackout —
   *  only then can it react / notify / go restore power. Reset when power's back. */
  witnessedBlackout: boolean;
  /** Seconds until this guard flips the next breaker while working the fusebox
   *  (staged power restore). Reset while travelling to the fusebox. */
  fuseTimer: number;
  /** True this tick when the guard has its flashlight on (dark tile, non-NV).
   *  Set by `computeFlashlights`; read by the renderer to draw the beam. */
  flashlightOn: boolean;
  /** Phase of the look-sweep (flashlight-in-dark AND active patrol sweep). */
  sweepPhase: number;
  // -- distraction (breaks patrol monotony; see updateGuardAwareness) --
  /** Ticks remaining while "zoned out" — vision range is collapsed until 0. */
  distractedTicks: number;
  /** Ticks until this guard is eligible to zone out again (spaces distractions). */
  distractCooldownTicks: number;
  // -- patrol --
  patrol: {
    route: PatrolRoute;
    waypointIdx: number;
  } | null;
  /** Cached A* result. Cleared/replanned on target change or when a step
   *  would step onto an occupied tile. */
  path: CachedPath | null;
  // -- phase-2 perception state --
  /** Awareness meter, 0..CONFIG.awareness.max. */
  awareness: number;
  /** Derived state per CLAUDE.md's ladder. Updated by `updateGuardAwareness`. */
  state: GuardState;
  /**
   * Tile at which the guard last saw the player. Rendered as a ghost marker
   * when the guard is cautious/alert and player is currently off-FOV.
   */
  lastKnownPlayerX: number | null;
  lastKnownPlayerY: number | null;
  /**
   * Where the guard wants to look / walk. Set by a sound hit or a fresh
   * sighting. Phase 4 pathfinds toward this on suspicious+.
   */
  investigationX: number | null;
  investigationY: number | null;
  /** True this tick if the guard currently has eyes on the player (drives the
   *  "SEEN!" stealth readout + the sighting ghost). Set in updateGuardAwareness. */
  seesPlayerNow: boolean;
  /** Ticks remaining on this guard's Sniper-Elite "seen" ghost (the tile it last
   *  saw the player = `lastKnownPlayer*`). While >0 it commits to revisiting that
   *  spot; a player-visible ghost marker renders there. */
  sightingGhostTicks: number;
  /**
   * Cached FOV tiles for this tick (indices into map.tiles). Written by
   * `updateGuardAwareness`; read by the perception overlay + visibility
   * checks. Kept on the entity to avoid recomputing between the sim pass
   * and the render pass.
   */
  fovTiles: Set<number> | null;
  /** Same 6-part body as the player — used for combat resolution + death. */
  body: BodyState;
  /** Guard's sidearm. Silenced pistol with unlimited ammo by default. */
  weapon: Weapon;
  /** What this entity is carrying. Every LIVE entity has pockets — janitors,
   *  civilians and the client are all structurally `Guard`, so one field covers
   *  them all, now and for whatever gets added later. Pickpocketed one item at
   *  a time (sim/pickpocket.ts); looted wholesale from the corpse. */
  pockets: Item[];
  /** Seconds until this guard needs the restroom. Counts down on duty; on
   *  reaching 0 they walk to one, spend a moment, and return to post. Nobody is
   *  posted to a restroom, so it's a room that's usually empty — and
   *  occasionally holds someone you didn't plan for. */
  reliefTimer: number;
  /** Where they're heading for relief, or null when on duty. */
  reliefX: number | null;
  reliefY: number | null;
  /** Seconds left in the restroom once arrived. */
  reliefDwell: number;
  /** A security station this guard mans (map.stations). Non-null replaces
   *  section roaming with standing the desk — see `manStation`. */
  stationX: number | null;
  stationY: number | null;
  /** True once a player has looted this guard's corpse; overlay + adjacency
   *  queries skip looted corpses so the loot only pays out once. */
  looted: boolean;
  /** True once this guard's corpse has been STASHED inside a prop (prop verb
   *  'stash'). A stashed body is out of the world: nobody spots it, nobody
   *  loots it, and no blood trail resolves against it. */
  stashed: boolean;
  /** Entity ids of bodies this guard has already found and reported — so a
   *  corpse in its patrol lane raises the alarm once (see corpseWatch.ts). */
  knownCorpses: Set<number>;
  // -- blood-trail tracking (sim/bloodTrail.ts) --
  /** Active blood-trail follow, or null. Non-null makes `updateGuard` step
   *  toward `bloodTrack.target` instead of patrolling. */
  bloodTrack: BloodTrackState | null;
  /** Ticks left before the guard will notice blood again (set when a track
   *  ends, so it doesn't instantly re-lock the same smear). */
  bloodCooldown: number;
  /** Freshness (BloodMap tick) up to which this guard has already investigated
   *  blood. It only notices blood NEWER than this, so it doesn't keep re-tracking
   *  the same old blood after the trail's been dealt with. */
  bloodSeenTick: number;
  /** Per-tile blood this guard has already walked/investigated (map indices).
   *  It never re-notices a tile in here — so once a trail is followed (and hit a
   *  dead end, a body, or the player), the guard won't loop back onto the same
   *  smear. Cleared only on respawn-scale resets. */
  bloodSeen: Set<number>;
  /** Health packs carried — spent to patch up a wounded guard found at the end
   *  of a blood trail. */
  healthPacks: number;
  /** Night-vision goggles currently ACTIVE — perceives the player using a
   *  boosted light level (sees in the dark) and doesn't hunker/blind-sweep in
   *  darkness. Auto-toggled by `updateNightVisionGoggles`: on when the tile is
   *  dark, and forced OFF (with a flash-blind) if the tile turns bright. */
  nightVision: boolean;
  /** Has NV goggles at all (the capability). The floor captain does. Only these
   *  guards auto-toggle `nightVision` on/off with the light. */
  hasNightVision: boolean;
  // -- section patrol + floor captain (sim/patrol.ts, sim/dispatch.ts) --
  /** Room this guard patrols, or null (captain / debug wanderer with no plan). */
  homeRoomId: number | null;
  /** Index of the guard's section within its room's partition, or null. */
  sectionId: number | null;
  /** The one floor captain: roams between rooms + drives dispatch. Owns no section. */
  isCaptain: boolean;
  /** Surplus guard sent to rest in the green room (no home room/section). Set by
   *  dispatch's reallocation; cleared when recalled or reassigned. */
  resting: boolean;
  /** The guard's section AABB + walkable anchor, copied on so `updateGuard` is
   *  self-contained (it never sees the FloorPatrol registry). null until assigned. */
  secX0: number | null;
  secY0: number | null;
  secX1: number | null;
  secY1: number | null;
  secAnchorX: number | null;
  secAnchorY: number | null;
  // room-scan state (sweep the vision cone across a new section before roaming):
  /** True while sweeping the current section; blocks roaming until coverage hits. */
  scanning: boolean;
  /** Section floor-tile indices seen so far this scan (null = not yet armed). */
  scanCovered: Set<number> | null;
  /** Non-opaque section tiles to cover — the scan-coverage denominator. */
  scanTargetCount: number;
  /** Which cardinal (0=E,1=N,2=W,3=S) the guard is currently holding while scanning. */
  scanDirIdx: number;
  /** Ticks held on the current cardinal. */
  scanHoldTicks: number;
  /** Countdown safety cap so an occluded section can't hang the scan. */
  scanTimeoutTicks: number;
  /** Pause counter at a captain roam waypoint (also reused by section roam). */
  roamDwellTicks: number;
  // -- FoW / marking --
  /**
   * Player-marked guard. Marked guards render at their current position
   * even when the player can't see them (with a desaturated tint).
   * Unmarked guards fall back to `lastSeen*` — a Sniper-Elite-style ghost
   * at the tile they were last observed on.
   */
  marked: boolean;
  /** True after the guard has been in the player's FOV at least once. */
  everSeenByPlayer: boolean;
  /** Last position + appearance snapshot from a tick the player saw them. */
  lastSeenX: number;
  lastSeenY: number;
  lastSeenGlyph: string;
  lastSeenFg: number;
  // -- door watch (sim/doorWatch.ts) --
  /** Keys ("x,y") of doors this guard has personally seen CLOSED — its memory of
   *  how the place should look. Grows as it patrols. */
  knownClosedDoors: Set<string>;
  /** A known-closed door the guard has since spotted OPEN and is walking over to
   *  shut, or null. Set by `updateDoorMemory`; consumed in `updateGuard`. */
  closeDoorTarget: { x: number; y: number } | null;
  /** Ticks until the guard will bother chasing another open door (anti-thrash). */
  doorCloseCooldown: number;
}

/** Deterministic-friendly random used by wander. Swap for a seeded PRNG later. */
function rand(): number {
  return Math.random();
}

export function spawnGuard(
  world: World,
  x: number,
  y: number,
  patrol: PatrolRoute | null = null,
): Guard {
  const base = world.spawn({
    glyph: 'g',
    // Neon alert red — style.ts COLOR_ALERT. Not imported to preserve
    // the render/sim separation (arch rule 4).
    fg: 0xff3355,
    bg: -1,
    x,
    y,
  });
  const g: Guard = {
    ...base,
    facingDx: 0,
    facingDy: 1,
    legDx: 0,
    legDy: 1,
    stance: 'walk',
    peekTimer: 0,
    peeking: false,
    hiddenByCover: false,
    perceivedByPlayer: false,
    wanderCooldown: 30 + Math.floor(rand() * 90),
    alertingTimer: 0,
    restorePower: false,
    coverTargetX: null,
    coverTargetY: null,
    coverSeekTimer: 0,
    bumpedPlayer: false,
    witnessedBlackout: false,
    fuseTimer: 0,
    flashlightOn: false,
    patrol: patrol && patrol.length > 0 ? { route: patrol, waypointIdx: 0 } : null,
    path: null,
    awareness: 0,
    state: 'unaware',
    lastKnownPlayerX: null,
    lastKnownPlayerY: null,
    investigationX: null,
    investigationY: null,
    seesPlayerNow: false,
    sightingGhostTicks: 0,
    fovTiles: null,
    body: createHumanoidBody(),
    weapon: createGuardPistol(),
    pockets: [],
    reliefTimer: CONFIG.guard.relief.everySec,
    reliefX: null,
    reliefY: null,
    reliefDwell: 0,
    stationX: null,
    stationY: null,
    looted: false,
    stashed: false,
    knownCorpses: new Set<number>(),
    bloodTrack: null,
    bloodCooldown: 0,
    bloodSeenTick: 0,
    bloodSeen: new Set(),
    healthPacks: CONFIG.blood.healthPacksPerGuard,
    nightVision: false,
    hasNightVision: false,
    sweepPhase: 0,
    // Start with a full interval on the clock so a freshly-spawned guard doesn't
    // zone out immediately (and keeps single-tick unit tests deterministic).
    distractedTicks: 0,
    distractCooldownTicks:
      CONFIG.guard.distract.minIntervalTicks +
      Math.floor(rand() * CONFIG.guard.distract.intervalJitterTicks),
    homeRoomId: null,
    sectionId: null,
    isCaptain: false,
    resting: false,
    secX0: null,
    secY0: null,
    secX1: null,
    secY1: null,
    secAnchorX: null,
    secAnchorY: null,
    scanning: false,
    scanCovered: null,
    scanTargetCount: 0,
    scanDirIdx: 0,
    scanHoldTicks: 0,
    scanTimeoutTicks: 0,
    roamDwellTicks: 0,
    marked: false,
    everSeenByPlayer: false,
    lastSeenX: x,
    lastSeenY: y,
    lastSeenGlyph: 'g',
    lastSeenFg: 0xff3355,
    knownClosedDoors: new Set(),
    closeDoorTarget: null,
    doorCloseCooldown: 0,
  };
  world.entities.set(g.id, g);
  return g;
}

/**
 * Sweep the flashlight (look direction) ±45° around the travel direction while a
 * guard patrols in the dark with a torch, so it scans a wider arc as it walks.
 * Legs stay pointed along travel (`legDx/Dy`); only the facing (cone + beam)
 * oscillates. Call after the movement pass. No-op if the guard isn't travelling.
 */
export function sweepFlashlightFacing(guard: Guard, tickDt: number): void {
  oscillateFacingAroundLegs(guard, tickDt, CONFIG.guard.flashlightSweepRate);
}

/**
 * Active patrol sweep. While an unaware guard patrols its region — whether
 * walking a roam leg or dwelling at a point — it oscillates its cone ±45° around
 * its travel direction so it *sweeps* the area rather than only looking where it
 * walks. Call after the movement pass (like the flashlight sweep). No-op if the
 * guard has no established travel direction yet.
 */
export function sweepPatrolFacing(guard: Guard, tickDt: number): void {
  oscillateFacingAroundLegs(guard, tickDt, CONFIG.guard.patrol.sweepRate);
}

/** Oscillate `facing` between legs−45°, legs, legs+45° via a sine of `sweepPhase`
 *  (legs = travel direction). Shared by the flashlight + patrol sweeps. */
function oscillateFacingAroundLegs(guard: Guard, tickDt: number, rate: number): void {
  const legIdx = dirIndex(guard.legDx, guard.legDy);
  if (legIdx < 0) return; // legs not on an 8-dir yet
  guard.sweepPhase += rate * tickDt;
  const off = Math.round(Math.sin(guard.sweepPhase)); // -1, 0, +1
  if (off === 0) {
    guard.facingDx = guard.legDx;
    guard.facingDy = guard.legDy;
    return;
  }
  const r = rotateFacing(guard.legDx, guard.legDy, off > 0 ? 1 : -1);
  guard.facingDx = r.dx;
  guard.facingDy = r.dy;
}

/** Guard tile-step speed (tiles/sec) for its current stance. Guards walk by
 *  default and slow to crouch speed while ducking behind cover. Leg crippling
 *  scales it exactly like the player's (one leg → half, both → immobile), so
 *  shooting out a guard's legs stops it walking. Also covers the janitor. */
export function guardSpeed(guard: Guard): number {
  const base = guard.stance === 'crouch' ? CONFIG.speed.crouch : CONFIG.speed.walk;
  return base * movementMultiplier(guard.body);
}

const DIRS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1],
];

/** Shoves a blocking ally out of (x, y) away from (fromX, fromY); true if cleared. */
type PushFn = (x: number, y: number, fromX: number, fromY: number) => boolean;

export interface UpdateGuardOptions {
  /** If true, skip pathing this tick. Used when the guard is holding a
   *  firing position (see guardShouldHoldGround). In-progress steps still
   *  finish so the guard doesn't get stuck mid-tile. */
  holdGround?: boolean;
  /** True at (x, y) iff the (live) player occupies it. Lets the guard notice
   *  when the PLAYER is the obstruction blocking its step (a bump). */
  playerAt?: (x: number, y: number) => boolean;
  /** Shove a blocking ALLY out of tile (x, y), away from (fromX, fromY). Returns
   *  true if the tile was cleared. Lets a boxed-in guard push a fellow guard/
   *  janitor aside instead of stalling. Supplied by main.ts (it owns the roster). */
  push?: (x: number, y: number, fromX: number, fromY: number) => boolean;
}

export function updateGuard(
  guard: Guard,
  map: MapData,
  tickDt: number,
  occupied: (x: number, y: number) => boolean,
  emitSound?: EmitSound,
  opts: UpdateGuardOptions = {},
): void {
  if (guard.body.dead) {
    // No glyph flip — renderer tilts the sprite 90° via `entity.tilt` and
    // applies a bleed-red tint at draw time. Keeps the same 'g' glyph so
    // death reads as "downed" not "corpse symbol".
    syncVisual(guard);
    return;
  }
  // Note doors we can see (builds "known closed" memory + picks one to shut).
  // Runs every live tick, whatever branch we take below, so memory + the close
  // cooldown stay current; the actual walk-and-shut fires only when unaware.
  updateDoorMemory(guard, map);
  if (guard.alertingTimer > 0) guard.alertingTimer = Math.max(0, guard.alertingTimer - tickDt);
  guard.bumpedPlayer = false;
  const playerAt = opts.playerAt;
  const push = opts.push;
  // Always finish an in-progress step regardless of state. Prevents the
  // guard from "freezing mid-tile" the instant they see the player.
  if (guard.moveProgress > 0) {
    const diag = guard.moveDx !== 0 && guard.moveDy !== 0;
    const rate = guardSpeed(guard) * (diag ? 1 / Math.SQRT2 : 1);
    guard.moveProgress += rate * tickDt;
    if (guard.moveProgress >= 1) {
      guard.x += guard.moveDx;
      guard.y += guard.moveDy;
      guard.moveProgress = 0;
      guard.moveDx = 0;
      guard.moveDy = 0;
      // Advance cached-path index to keep it aligned with guard.x/y.
      if (guard.path && guard.path.idx < guard.path.steps.length - 1) guard.path.idx++;
    }
    syncVisual(guard);
    return;
  }

  if (opts.holdGround) {
    // Alert + has-shot: don't reposition, just fire. Combat is handled in
    // main.ts's per-tick pass.
    syncVisual(guard);
    return;
  }

  // Flash-blinded + still in the stun window → dazed, can't move (it finished
  // any in-progress step above). It's also blind (no sighting — see awareness).
  if (isBlindStunned(guard)) {
    syncVisual(guard);
    return;
  }

  // Power-restore duty (blackout) overrides everything else: beeline to the
  // fusebox. main.ts flips the breakers back on once we arrive adjacent to it.
  if (guard.restorePower && map.fusebox) {
    moveTowards(guard, map.fusebox.x, map.fusebox.y, map, occupied, tickDt, { adjacent: true, playerAt, push });
    syncVisual(guard);
    return;
  }

  // Following a blood trail: step toward the current trail tile. The tracker
  // logic (sim/bloodTrail.ts) picks the target + resolves the end; here we just
  // walk to it. Takes priority over patrol/wander/dark-hunker so a spotted
  // trail is pursued even in an unaware state or the dark.
  if (guard.bloodTrack) {
    const t = guard.bloodTrack;
    if (guard.x !== t.targetX || guard.y !== t.targetY) {
      moveTowards(guard, t.targetX, t.targetY, map, occupied, tickDt, { playerAt, push });
    }
    syncVisual(guard);
    return;
  }

  // Resting in the green room (surplus guard, off duty): walk to the rest point
  // and idle. Cleared when the captain recalls or reassigns (dispatch.ts).
  if (guard.resting) {
    if (guard.coverTargetX !== null && guard.coverTargetY !== null &&
        (guard.x !== guard.coverTargetX || guard.y !== guard.coverTargetY)) {
      moveTowards(guard, guard.coverTargetX, guard.coverTargetY, map, occupied, tickDt, { playerAt, push });
    }
    syncVisual(guard);
    return;
  }

  // In the dark and unbothered, guards hunker at cover ONLY if they can't see —
  // i.e. no night vision AND no flashlight. A guard with a torch (flashlightOn)
  // patrols on, sweeping its beam to scan as it walks carefully (see the sweep
  // in main.ts after the movement pass).
  if (
    guard.state === 'unaware' &&
    !guard.nightVision &&
    !guard.flashlightOn &&
    lightLevelAt(map, guard.x, guard.y) < CONFIG.light.hideBelow
  ) {
    hunkerInDark(guard, map, occupied, tickDt, playerAt, push);
    syncVisual(guard);
    return;
  }

  if (guard.state !== 'unaware') {
    // No investigation target (a spent "seen" ghost that escalated to a sweep) →
    // roam-search the floor, regardless of light.
    if (guard.investigationX === null || guard.investigationY === null) {
      sweepDark(guard, map, occupied, tickDt, playerAt, push);
      syncVisual(guard);
      return;
    }
    // Moving in the dark with no active player track → search the area in
    // ALERT-style sweeps (roam between points) rather than standing idle. A
    // guard chasing a known player (lastKnown set) still beelines it.
    const chasingPlayer = guard.lastKnownPlayerX !== null && guard.lastKnownPlayerY !== null;
    if (isUnlit(map, guard.x, guard.y) && !chasingPlayer && !guard.nightVision) {
      sweepDark(guard, map, occupied, tickDt, playerAt, push);
      syncVisual(guard);
      return;
    }
    // Suspicious+: A* toward the investigation target. Alert guards get a
    // cover-preferring cost bias so they route through cubicles rather
    // than sprint down open aisles.
    if (guard.investigationX !== null && guard.investigationY !== null) {
      const useCover = guard.state === 'alert';
      const mtOpts: {
        adjacent?: boolean;
        costBias?: (x: number, y: number) => number;
        playerAt?: ((x: number, y: number) => boolean) | undefined;
        push?: ((x: number, y: number, fromX: number, fromY: number) => boolean) | undefined;
      } = { adjacent: true, playerAt, push };
      if (useCover) {
        mtOpts.costBias = coverCostBias(
          map,
          guard.investigationX,
          guard.investigationY,
          CONFIG.pathing.openTilePenaltyAlert,
        );
      }
      moveTowards(guard, guard.investigationX, guard.investigationY, map, occupied, tickDt, mtOpts);
    }
    syncVisual(guard);
    return;
  }

  // Unaware — nature calls. Checked BEFORE door-tidying: a guard who opens a
  // door on the way to the restroom would otherwise turn round to shut it, and
  // never get there. Anything above this (investigating, blood, blackout duty)
  // still outranks it.
  if (updateRelief(guard, map, occupied, tickDt, playerAt, push)) {
    syncVisual(guard);
    return;
  }

  // Unaware + able to see — a stationed guard that spotted a door it knew closed
  // now standing open walks over and shuts it before carrying on its patrol.
  if (tryCloseKnownDoor(guard, map, occupied, tickDt, emitSound, { playerAt, push })) {
    syncVisual(guard);
    return;
  }

  // Unaware — the floor captain roams between rooms (dispatch sets its target).
  if (guard.isCaptain) {
    captainRoam(guard, map, occupied, tickDt, playerAt, push);
    syncVisual(guard);
    return;
  }

  // Unaware — a guard on the security desk stands it instead of roaming.
  if (guard.stationX !== null && guard.stationY !== null) {
    manStation(guard, map, occupied, tickDt, playerAt, push);
    syncVisual(guard);
    return;
  }

  // Unaware — a guard assigned to a room section scans it then roams within it.
  if (guard.homeRoomId !== null && guard.secX0 !== null) {
    patrolSection(guard, map, occupied, tickDt, playerAt, push);
    syncVisual(guard);
    return;
  }

  // Unaware — patrol if we have a route; otherwise legacy random wander.
  if (guard.patrol && guard.patrol.route.length > 0) {
    const target = guard.patrol.route[guard.patrol.waypointIdx]!;
    if (guard.x === target.x && guard.y === target.y) {
      // Arrived at waypoint — brief pause, then advance.
      if (guard.wanderCooldown > 0) {
        guard.wanderCooldown -= 1;
      } else {
        guard.patrol.waypointIdx = (guard.patrol.waypointIdx + 1) % guard.patrol.route.length;
        guard.wanderCooldown = 30 + Math.floor(rand() * 30);
        guard.path = null;
      }
    } else {
      moveTowards(guard, target.x, target.y, map, occupied, tickDt, { playerAt, push });
    }
    syncVisual(guard);
    return;
  }

  // Legacy wander for debug-spawned guards (no patrol route).
  if (guard.wanderCooldown > 0) {
    guard.wanderCooldown -= 1;
  } else {
    guard.wanderCooldown = 30 + Math.floor(rand() * 120);
    if (rand() < 0.55) {
      const idx = Math.floor(rand() * DIRS.length);
      const dir = DIRS[idx]!;
      const nx = guard.x + dir[0];
      const ny = guard.y + dir[1];
      if (playerAt?.(nx, ny)) guard.bumpedPlayer = true; // wandered into the player
      if (!isBlocked(map, nx, ny) && !occupied(nx, ny)) {
        guard.moveDx = dir[0];
        guard.moveDy = dir[1];
        guard.legDx = dir[0];
        guard.legDy = dir[1];
        const diag = dir[0] !== 0 && dir[1] !== 0;
        const rate = guardSpeed(guard) * (diag ? 1 / Math.SQRT2 : 1);
        guard.moveProgress = rate * tickDt;
        guard.facingDx = dir[0];
        guard.facingDy = dir[1];
        emitSound?.(guard.x, guard.y, CONFIG.sound.loudness.footstepWalk, guard.id);
      }
    }
  }
  syncVisual(guard);
}

/** Cardinal scan order: E, N, W, S (matches `scanDirIdx`). */
const SCAN_DIRS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [0, -1],
  [-1, 0],
  [0, 1],
];

/**
 * Section patrol (unaware). Walk to the section, SCAN it (sweep the vision cone
 * across the cardinals until enough of its floor is covered or a timeout), then
 * roam within it. The captain owns no section and is handled by `captainRoam`.
 */
function patrolSection(
  guard: Guard,
  map: MapData,
  occupied: (x: number, y: number) => boolean,
  tickDt: number,
  playerAt?: (x: number, y: number) => boolean,
  push?: PushFn,
): void {
  const x0 = guard.secX0!;
  const y0 = guard.secY0!;
  const x1 = guard.secX1!;
  const y1 = guard.secY1!;
  const inSection = guard.x >= x0 && guard.x <= x1 && guard.y >= y0 && guard.y <= y1;
  if (!inSection) {
    // Not on station yet → head to the anchor and arm a fresh scan for arrival.
    guard.scanning = true;
    guard.scanCovered = null;
    if (guard.secAnchorX !== null && guard.secAnchorY !== null) {
      moveTowards(guard, guard.secAnchorX, guard.secAnchorY, map, occupied, tickDt, { playerAt, push });
    }
    return;
  }
  if (guard.scanning) {
    scanSection(guard, map, x0, y0, x1, y1);
    return; // hold position while sweeping
  }
  roamSection(guard, map, occupied, tickDt, x0, y0, x1, y1, playerAt, push);
}

/**
 * Relief breaks. Returns true if the guard is dealing with it this tick.
 *
 * Nobody is POSTED to a restroom (patrol.ts NEVER_POSTED), so this is the only
 * reason a guard is ever in one — which makes a bathroom a room that is usually
 * empty and occasionally, unpredictably, not. The timer only runs while unaware,
 * so a guard who is searching for you never wanders off mid-hunt.
 */
function updateRelief(
  guard: Guard,
  map: MapData,
  occupied: (x: number, y: number) => boolean,
  tickDt: number,
  playerAt?: (x: number, y: number) => boolean,
  push?: PushFn,
): boolean {
  const cfg = CONFIG.guard.relief;
  // In the restroom: finish up, then back on duty.
  if (guard.reliefDwell > 0) {
    guard.reliefDwell -= tickDt;
    if (guard.reliefDwell <= 0) {
      guard.reliefX = null;
      guard.reliefY = null;
      guard.reliefTimer = cfg.everySec + Math.random() * cfg.jitterSec;
      guard.scanning = true; // re-scan the section on return
      guard.scanCovered = null;
    }
    return true;
  }
  // On the way.
  if (guard.reliefX !== null && guard.reliefY !== null) {
    if (guard.x === guard.reliefX && guard.y === guard.reliefY) {
      guard.reliefDwell = cfg.dwellSec;
      return true;
    }
    moveTowards(guard, guard.reliefX, guard.reliefY, map, occupied, tickDt, { playerAt, push });
    return true;
  }
  // On duty.
  guard.reliefTimer -= tickDt;
  if (guard.reliefTimer > 0) return false;
  const loo = nearestRestroom(map, guard.x, guard.y);
  if (!loo) {
    guard.reliefTimer = cfg.everySec; // no facilities on this floor; hold on
    return false;
  }
  guard.reliefX = loo.x;
  guard.reliefY = loo.y;
  guard.path = null;
  return true;
}

/** A walkable tile in the nearest restroom, or null if the floor has none. */
function nearestRestroom(map: MapData, x: number, y: number): { x: number; y: number } | null {
  let bestRoom: { x0: number; y0: number; x1: number; y1: number } | null = null;
  let bestD = Infinity;
  for (const room of map.rooms) {
    if (room.kind !== 'restroom') continue;
    const cx = Math.round((room.x0 + room.x1) / 2);
    const cy = Math.round((room.y0 + room.y1) / 2);
    const d = Math.abs(cx - x) + Math.abs(cy - y);
    if (d < bestD) { bestD = d; bestRoom = room; }
  }
  if (!bestRoom) return null;
  for (let ty = bestRoom.y0 + 1; ty <= bestRoom.y1 - 1; ty++) {
    for (let tx = bestRoom.x0 + 1; tx <= bestRoom.x1 - 1; tx++) {
      if (!isBlocked(map, tx, ty)) return { x: tx, y: ty };
    }
  }
  return null; // furnished solid, somehow
}

/**
 * Stand the security desk: walk back to it if displaced, then sweep the cone
 * across the cardinals indefinitely — re-arming the scan each time it completes
 * rather than wandering off like a section guard.
 *
 * A manned station is a FIXED hazard: the player can learn where it is and route
 * around it, which is what makes the security office worth scouting instead of
 * just unlucky to walk into.
 */
function manStation(
  guard: Guard,
  map: MapData,
  occupied: (x: number, y: number) => boolean,
  tickDt: number,
  playerAt?: (x: number, y: number) => boolean,
  push?: PushFn,
): void {
  const sx = guard.stationX!;
  const sy = guard.stationY!;
  if (guard.x !== sx || guard.y !== sy) {
    guard.scanning = true;
    guard.scanCovered = null;
    moveTowards(guard, sx, sy, map, occupied, tickDt, { playerAt, push });
    return;
  }
  // At the desk. Sweep whatever the station can see — the section rect if it has
  // one, else the room around it.
  const x0 = guard.secX0 ?? sx - CONFIG.guard.station.watchRadius;
  const y0 = guard.secY0 ?? sy - CONFIG.guard.station.watchRadius;
  const x1 = guard.secX1 ?? sx + CONFIG.guard.station.watchRadius;
  const y1 = guard.secY1 ?? sy + CONFIG.guard.station.watchRadius;
  if (!guard.scanning) {
    guard.scanning = true;
    guard.scanCovered = null;
  }
  scanSection(guard, map, x0, y0, x1, y1);
}

/** Sweep the cone across cardinals, accumulating covered section tiles; ends the
 *  scan on sufficient coverage or timeout. Sets facing; never moves. */
function scanSection(
  guard: Guard,
  map: MapData,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): void {
  const cfg = CONFIG.guard.patrol;
  if (guard.scanCovered === null) {
    guard.scanCovered = new Set();
    guard.scanTargetCount = countScannable(map, x0, y0, x1, y1);
    guard.scanDirIdx = 0;
    guard.scanHoldTicks = 0;
    guard.scanTimeoutTicks = cfg.scanTimeoutTicks;
    const d0 = SCAN_DIRS[0]!;
    guard.facingDx = d0[0];
    guard.facingDy = d0[1];
  }
  // Fold this tick's cone (computed by the awareness pass) into coverage.
  if (guard.fovTiles) {
    for (const idx of guard.fovTiles) {
      const tx = idx % map.width;
      const ty = Math.floor(idx / map.width);
      if (tx < x0 || tx > x1 || ty < y0 || ty > y1) continue;
      if (blocksVision(tileAt(map, tx, ty))) continue;
      guard.scanCovered.add(idx);
    }
  }
  // Advance to the next cardinal after holding this one long enough.
  guard.scanHoldTicks += 1;
  if (guard.scanHoldTicks >= cfg.scanHoldTicks) {
    guard.scanDirIdx = (guard.scanDirIdx + 1) % SCAN_DIRS.length;
    guard.scanHoldTicks = 0;
    const d = SCAN_DIRS[guard.scanDirIdx]!;
    guard.facingDx = d[0];
    guard.facingDy = d[1];
  }
  guard.scanTimeoutTicks -= 1;
  const frac = guard.scanCovered.size / guard.scanTargetCount;
  if (frac >= cfg.scanCoverageFrac || guard.scanTimeoutTicks <= 0) {
    guard.scanning = false;
    guard.scanCovered = null;
  }
}

/** Count non-opaque tiles in a rect — the scan-coverage denominator. */
function countScannable(map: MapData, x0: number, y0: number, x1: number, y1: number): number {
  let n = 0;
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      if (inBounds(map, x, y) && !blocksVision(tileAt(map, x, y))) n++;
    }
  }
  return Math.max(1, n);
}

/** Roam between random walkable points inside the section, pausing at each. */
function roamSection(
  guard: Guard,
  map: MapData,
  occupied: (x: number, y: number) => boolean,
  tickDt: number,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  playerAt?: (x: number, y: number) => boolean,
  push?: PushFn,
): void {
  const cfg = CONFIG.guard.patrol;
  const tgt = guard.coverTargetX;
  const tgtY = guard.coverTargetY;
  const targetInSection = tgt !== null && tgtY !== null && tgt >= x0 && tgt <= x1 && tgtY >= y0 && tgtY <= y1;
  const atTarget = tgt === guard.x && tgtY === guard.y;
  if (!targetInSection || (atTarget && guard.roamDwellTicks >= cfg.roamDwellTicks) || (tgt !== null && tgtY !== null && isBlocked(map, tgt, tgtY))) {
    if (atTarget && guard.roamDwellTicks < cfg.roamDwellTicks) {
      guard.roamDwellTicks += 1; // dwell at the current point before moving on
      return;
    }
    const spot = pickSectionRoamTarget(guard, map, cfg.roamRadius, x0, y0, x1, y1);
    guard.coverTargetX = spot.x;
    guard.coverTargetY = spot.y;
    guard.roamDwellTicks = 0;
    guard.path = null;
  }
  if (guard.coverTargetX === guard.x && guard.coverTargetY === guard.y) return;
  moveTowards(guard, guard.coverTargetX!, guard.coverTargetY!, map, occupied, tickDt, { playerAt, push });
}

/** A random walkable tile within `radius` of the guard, clamped to the section. */
function pickSectionRoamTarget(
  guard: Guard,
  map: MapData,
  radius: number,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): Point {
  for (let tries = 0; tries < 16; tries++) {
    const tx = clamp(guard.x + Math.round((rand() * 2 - 1) * radius), x0, x1);
    const ty = clamp(guard.y + Math.round((rand() * 2 - 1) * radius), y0, y1);
    if (inBounds(map, tx, ty) && !isBlocked(map, tx, ty)) return { x: tx, y: ty };
  }
  return { x: guard.secAnchorX ?? guard.x, y: guard.secAnchorY ?? guard.y };
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * Floor-captain roam (unaware). `updateDispatch` sets `coverTargetX/Y` to the
 * current room-centre waypoint and advances it on arrival + dwell; here we just
 * walk there.
 */
function captainRoam(
  guard: Guard,
  map: MapData,
  occupied: (x: number, y: number) => boolean,
  tickDt: number,
  playerAt?: (x: number, y: number) => boolean,
  push?: PushFn,
): void {
  if (guard.coverTargetX === null || guard.coverTargetY === null) return;
  if (guard.coverTargetX === guard.x && guard.coverTargetY === guard.y) return;
  moveTowards(guard, guard.coverTargetX, guard.coverTargetY, map, occupied, tickDt, { playerAt, push });
}

/**
 * Alert-style dark sweep: a guard searching the dark roams between points,
 * scanning as it goes (facing follows travel). Reuses the coverTarget fields as
 * sweep waypoints; re-picks on arrival or when the timer lapses.
 */
function sweepDark(
  guard: Guard,
  map: MapData,
  occupied: (x: number, y: number) => boolean,
  tickDt: number,
  playerAt?: (x: number, y: number) => boolean,
  push?: PushFn,
): void {
  const atTarget = guard.coverTargetX === guard.x && guard.coverTargetY === guard.y;
  const needTarget =
    guard.coverTargetX === null ||
    guard.coverTargetY === null ||
    atTarget ||
    guard.coverSeekTimer <= 0 ||
    isBlocked(map, guard.coverTargetX, guard.coverTargetY);
  if (needTarget) {
    const spot = pickSweepTarget(guard, map);
    guard.coverTargetX = spot?.x ?? null;
    guard.coverTargetY = spot?.y ?? null;
    guard.coverSeekTimer = 120 + Math.floor(rand() * 150); // ~2–4.5 s per leg
    guard.path = null;
  }
  if (guard.coverSeekTimer > 0) guard.coverSeekTimer -= 1;
  if (guard.coverTargetX === null || guard.coverTargetY === null) return;
  if (guard.coverTargetX === guard.x && guard.coverTargetY === guard.y) return;
  moveTowards(guard, guard.coverTargetX, guard.coverTargetY, map, occupied, tickDt, { playerAt, push });
}

/** A random walkable tile a moderate distance from the guard — a sweep leg. */
function pickSweepTarget(guard: Guard, map: MapData): Point | null {
  for (let tries = 0; tries < 12; tries++) {
    const ang = rand() * Math.PI * 2;
    const r = 5 + rand() * 6;
    const tx = Math.round(guard.x + Math.cos(ang) * r);
    const ty = Math.round(guard.y + Math.sin(ang) * r);
    if (inBounds(map, tx, ty) && !isBlocked(map, tx, ty)) return { x: tx, y: ty };
  }
  return null;
}

/**
 * Dark hunker behaviour: a guard that can't see anything settles beside cover
 * and periodically repositions to a *different* nearby cover spot, so it reads
 * as "taking cover and changing cover" rather than patrolling blindly.
 */
function hunkerInDark(
  guard: Guard,
  map: MapData,
  occupied: (x: number, y: number) => boolean,
  tickDt: number,
  playerAt?: (x: number, y: number) => boolean,
  push?: PushFn,
): void {
  const atTarget = guard.coverTargetX === guard.x && guard.coverTargetY === guard.y;
  const needTarget =
    guard.coverTargetX === null ||
    guard.coverTargetY === null ||
    (atTarget && guard.coverSeekTimer <= 0) ||
    isBlocked(map, guard.coverTargetX, guard.coverTargetY);
  if (needTarget) {
    const spot = nearestCoverStand(map, guard.x, guard.y, 8, occupied);
    if (spot) {
      guard.coverTargetX = spot.x;
      guard.coverTargetY = spot.y;
    } else {
      guard.coverTargetX = null;
      guard.coverTargetY = null;
    }
    guard.coverSeekTimer = 240 + Math.floor(rand() * 240); // ~4–8 s between moves
  }
  if (guard.coverSeekTimer > 0) guard.coverSeekTimer -= 1;
  if (guard.coverTargetX === null || guard.coverTargetY === null) return; // hold in place
  if (guard.coverTargetX === guard.x && guard.coverTargetY === guard.y) return; // arrived → hold
  moveTowards(guard, guard.coverTargetX, guard.coverTargetY, map, occupied, tickDt, { playerAt, push });
}

/**
 * Nearest walkable tile beside a wall/cover anchor within Chebyshev `radius`
 * of (x, y) — where a guard would take cover. Prefers spots it isn't already
 * standing on so it actually moves. null if none.
 */
function nearestCoverStand(
  map: MapData,
  x: number,
  y: number,
  radius: number,
  occupied: (x: number, y: number) => boolean,
): Point | null {
  let best: Point | null = null;
  let bestD = Infinity;
  for (let dy = -radius; dy <= radius; dy++) {
    for (let dx = -radius; dx <= radius; dx++) {
      if (dx === 0 && dy === 0) continue;
      const tx = x + dx;
      const ty = y + dy;
      if (isBlocked(map, tx, ty) || occupied(tx, ty)) continue;
      // Must sit next to a cover anchor (wall / half wall / cubicle).
      let besideCover = false;
      for (const [ox, oy] of DIRS) {
        if (isCoverAnchor(tileAt(map, tx + ox, ty + oy))) { besideCover = true; break; }
      }
      if (!besideCover) continue;
      const d = dx * dx + dy * dy;
      if (d < bestD) { bestD = d; best = { x: tx, y: ty }; }
    }
  }
  return best;
}

/**
 * Door-watch action: if the guard/janitor has a door to shut (set by
 * `updateDoorMemory`), walk to it and pull it closed. Returns true when it took
 * over this tick's movement (the caller should `syncVisual` + return). Shared by
 * `updateGuard` and the janitor so both tidy up doors the same way.
 *
 * The guard closes only from an ADJACENT tile — `moveTowards(..., adjacent:true)`
 * keeps it off the door tile, so shutting the door never seals it in. Closing
 * recomputes the light field (a shut door occludes light) and emits a soft
 * sound; a short cooldown stops it re-chasing a door that keeps getting reopened.
 */
export function tryCloseKnownDoor(
  guard: Guard,
  map: MapData,
  occupied: (x: number, y: number) => boolean,
  tickDt: number,
  emitSound?: EmitSound,
  opts: {
    playerAt?: ((x: number, y: number) => boolean) | undefined;
    push?: ((x: number, y: number, fromX: number, fromY: number) => boolean) | undefined;
  } = {},
): boolean {
  const d = guard.closeDoorTarget;
  if (!d) return false;
  if (tileAt(map, d.x, d.y) !== TileType.Door) {
    guard.closeDoorTarget = null; // already shut (by us earlier, or someone else)
    return false;
  }
  const dist = Math.max(Math.abs(guard.x - d.x), Math.abs(guard.y - d.y));
  if (dist === 0) return false; // standing on the open door — let patrol carry us off
  if (dist === 1) {
    if (toggleDoor(map, d.x, d.y) === 'closed') {
      computeLightField(map);
      emitSound?.(d.x, d.y, CONFIG.guard.doorWatch.closeSound, guard.id);
    }
    guard.facingDx = Math.sign(d.x - guard.x);
    guard.facingDy = Math.sign(d.y - guard.y);
    guard.closeDoorTarget = null;
    guard.doorCloseCooldown = CONFIG.guard.doorWatch.cooldownTicks;
    guard.path = null;
    return true;
  }
  moveTowards(guard, d.x, d.y, map, occupied, tickDt, {
    adjacent: true,
    playerAt: opts.playerAt,
    push: opts.push,
  });
  return true;
}

/**
 * Try to initiate a single step toward (tx, ty) using cached A*. Replans
 * when the target changes, the cache is exhausted, or the next tile is
 * occupied. Sets moveDx/Dy/moveProgress + facing on success.
 */
export function moveTowards(
  guard: Guard,
  tx: number,
  ty: number,
  map: MapData,
  occupied: (x: number, y: number) => boolean,
  tickDt: number,
  opts: {
    adjacent?: boolean;
    costBias?: (x: number, y: number) => number;
    playerAt?: ((x: number, y: number) => boolean) | undefined;
    /** Shove an ALLY out of tile (x, y) — away from (fromX, fromY) if possible.
     *  Returns true if the tile was cleared. Used when a guard is boxed in and
     *  can't route around a fellow guard/janitor. */
    push?: ((x: number, y: number, fromX: number, fromY: number) => boolean) | undefined;
  } = {},
): void {
  const adjacent = opts.adjacent ?? false;
  const needReplan =
    guard.path === null ||
    guard.path.targetX !== tx ||
    guard.path.targetY !== ty ||
    guard.path.adjacent !== adjacent ||
    guard.path.idx >= guard.path.steps.length - 1 ||
    // Path desynced from actual guard position (e.g. teleport / bumped).
    guard.path.steps[guard.path.idx]?.x !== guard.x ||
    guard.path.steps[guard.path.idx]?.y !== guard.y;

  if (needReplan) {
    // Don't use `occupied` inside A* — other guards may move each tick and
    // routing around them permanently causes weird detours. Just check
    // occupancy at step time and wait a beat if the next tile is taken.
    const pfOpts: Parameters<typeof findPath>[3] = { adjacentGoal: adjacent };
    if (opts.costBias) pfOpts.costBias = opts.costBias;
    const steps = findPath(
      { x: guard.x, y: guard.y },
      { x: tx, y: ty },
      (x, y) => !isBlocked(map, x, y) || npcOpenableDoor(map, x, y),
      pfOpts,
    );
    if (!steps || steps.length < 2) {
      guard.path = null;
      return;
    }
    guard.path = { steps, idx: 0, targetX: tx, targetY: ty, adjacent };
  }
  const path = guard.path!;
  let next = path.steps[path.idx + 1]!;
  if (occupied(next.x, next.y)) {
    // Blocked. If it's the PLAYER in the way, flag the bump so the guard can
    // react (they walked right into you) and wait.
    if (opts.playerAt?.(next.x, next.y)) { guard.bumpedPlayer = true; guard.path = null; return; }
    // An ALLY (or corpse-free obstruction) is in the way. First try to WALK
    // AROUND it: one-shot replan that avoids everything occupied right now
    // (the goal tile itself stays allowed so an occupied target still routes).
    const detourOpts: Parameters<typeof findPath>[3] = { adjacentGoal: adjacent };
    if (opts.costBias) detourOpts.costBias = opts.costBias;
    const detour = findPath(
      { x: guard.x, y: guard.y },
      { x: tx, y: ty },
      (x, y) => (!isBlocked(map, x, y) || npcOpenableDoor(map, x, y)) && (!occupied(x, y) || (x === tx && y === ty)),
      detourOpts,
    );
    if (detour && detour.length >= 2 && !occupied(detour[1]!.x, detour[1]!.y)) {
      guard.path = { steps: detour, idx: 0, targetX: tx, targetY: ty, adjacent };
      next = detour[1]!;
    } else if (opts.push?.(next.x, next.y, guard.x, guard.y)) {
      // Boxed in — shoved the ally aside; the tile is free now, step into it.
    } else {
      guard.path = null; // truly stuck — wait a beat and replan next tick.
      return;
    }
  }
  // Walking into a closed (unlocked, normal) door → open it as we pass, and
  // recompute light since it no longer occludes. Rare (only at a door tile).
  if (npcOpenableDoor(map, next.x, next.y)) {
    toggleDoor(map, next.x, next.y);
    computeLightField(map);
  }
  const dx = next.x - guard.x;
  const dy = next.y - guard.y;
  guard.moveDx = dx;
  guard.moveDy = dy;
  guard.legDx = dx;
  guard.legDy = dy;
  const diag = dx !== 0 && dy !== 0;
  const rate = guardSpeed(guard) * (diag ? 1 / Math.SQRT2 : 1);
  guard.moveProgress = rate * tickDt;
  guard.facingDx = dx;
  guard.facingDy = dy;
}
